import type { Address, Hex } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import type {
  SiweEvmSignerProfile,
  SyntheticDevAuthProfile,
} from "./app-auth-runtime";

const EVM_PRIVATE_KEY_PATTERN = /^0x[0-9a-fA-F]{64}$/u;
const EVM_ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/u;

export type SyntheticDevEvmSignerState =
  | {
      address: Address;
      enabled: true;
      privateKey: Hex;
      reason: null;
    }
  | {
      address: null;
      enabled: false;
      privateKey: null;
      reason: string;
    };

type EvmSignerProfile = {
  readonly evmPrivateKey: string | null;
  readonly walletAddress: string;
};

// The dev/test gate below must stay a directly-foldable literal expression:
// Vite replaces `import.meta.env.DEV` / `import.meta.env.MODE` with literals at
// build time, so in production the condition folds to `false`, Rollup proves the
// guarded branch dead, and both the inlined
// `VITE_GLIDER_DEV_EVM_SIGNER_PRIVATE_KEY` literal AND the `privateKeyToAccount`
// call are dropped — which lets `viem/accounts` (secp256k1 + @noble, ~kBs gzip)
// tree-shake out of the initial bundle entirely. NOTE: keep this as an inline
// `import.meta.env.*` expression, not a `const` or helper function — routing it
// through a function call defeats Rollup's dead-branch analysis and drags viem
// back into the eager graph.
const RAW_SYNTHETIC_DEV_EVM_PRIVATE_KEY: string | undefined =
  import.meta.env.DEV || import.meta.env.MODE === "test"
    ? import.meta.env.VITE_GLIDER_DEV_EVM_SIGNER_PRIVATE_KEY
    : undefined;

function normalizeWalletAddress(address: string): string {
  return address.startsWith("0x") ? address.toLowerCase() : address;
}

export function normalizeSyntheticDevEvmPrivateKey(
  value: string | null | undefined,
): Hex | null {
  const trimmed = value?.trim();
  if (!trimmed || !EVM_PRIVATE_KEY_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed as Hex;
}

function readEnvSyntheticDevEvmPrivateKey(): Hex | null {
  return normalizeSyntheticDevEvmPrivateKey(RAW_SYNTHETIC_DEV_EVM_PRIVATE_KEY);
}

// Only reachable behind the foldable dev/test gates inside the exported
// wrappers below. In production every call site folds dead, this function is
// dropped, and `viem/accounts` tree-shakes out of the eager graph — never pass
// the gate in as a parameter (see the note above).
function resolveEvmSigner(
  profile: EvmSignerProfile,
  input: {
    readonly missingKeyReason: string;
    readonly useDevEnvFallback: boolean;
  },
): SyntheticDevEvmSignerState {
  const privateKey =
    normalizeSyntheticDevEvmPrivateKey(profile.evmPrivateKey) ??
    (input.useDevEnvFallback ? readEnvSyntheticDevEvmPrivateKey() : null);
  if (!privateKey) {
    return {
      address: null,
      enabled: false,
      privateKey: null,
      reason: input.missingKeyReason,
    };
  }

  const expectedAddress = normalizeWalletAddress(profile.walletAddress);
  if (!EVM_ADDRESS_PATTERN.test(expectedAddress)) {
    return {
      address: null,
      enabled: false,
      privateKey: null,
      reason: "Synthetic EVM signing requires an EVM primary wallet.",
    };
  }

  const account = privateKeyToAccount(privateKey);
  if (normalizeWalletAddress(account.address) !== expectedAddress) {
    return {
      address: null,
      enabled: false,
      privateKey: null,
      reason:
        "Synthetic EVM signer private key does not match the primary wallet.",
    };
  }

  return {
    address: account.address,
    enabled: true,
    privateKey,
    reason: null,
  };
}

function disabledSigner(reason: string): SyntheticDevEvmSignerState {
  return {
    address: null,
    enabled: false,
    privateKey: null,
    reason,
  };
}

export function resolveSyntheticDevEvmSigner(
  profile: Pick<
    SyntheticDevAuthProfile,
    "evmPrivateKey" | "walletAddress"
  > | null,
): SyntheticDevEvmSignerState {
  if (!profile) {
    return disabledSigner("Synthetic dev auth is not active.");
  }

  if (!(import.meta.env.DEV || import.meta.env.MODE === "test")) {
    return disabledSigner(
      "Synthetic EVM signing is only available in local/test builds.",
    );
  }

  return resolveEvmSigner(profile, {
    missingKeyReason: "Synthetic EVM signing requires a local private key.",
    useDevEnvFallback: true,
  });
}

export function resolveWalletlessSiweEvmSigner(
  profile: SiweEvmSignerProfile | null,
): SyntheticDevEvmSignerState {
  if (!profile) {
    return disabledSigner("No Walletless SIWE signer is configured for this tab.");
  }

  if (
    !(
      import.meta.env.DEV ||
      import.meta.env.MODE === "test" ||
      import.meta.env.VITE_ENABLE_WALLETLESS_SIWE_E2E === "1"
    )
  ) {
    return disabledSigner("Walletless SIWE signing is disabled in this build.");
  }

  return resolveEvmSigner(profile, {
    missingKeyReason: "Walletless SIWE signing requires a private key.",
    useDevEnvFallback: false,
  });
}
